import {
  Box,
  Typography,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Card,
  CardContent,
  Alert,
  Button,
  Paper,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import patient360Data from '../../../mocks/patient360Data.json';

export interface FoodClassification {
  id: string;
  customFoodName: string;
  classification: 'GREEN' | 'YELLOW' | 'RED';
  portion: string;
  notes: string;
}

export interface ProtocolRule {
  id: string;
  ruleType: 'MEAL_TIMING' | 'PORTION_LIMIT' | 'PAIRING' | 'AVOIDANCE' | 'CUSTOM';
  description: string;
  parameters: Record<string, string | number | boolean>;
  isActive: boolean;
}

export type AssignedPatient = (typeof patient360Data.assignedPatients)[0];

interface StepContentArgs {
  step: number;
  selectedPatient: AssignedPatient | null;
  setSelectedPatient: (patient: AssignedPatient) => void;
  protocolTitle: string;
  setProtocolTitle: (value: string) => void;
  protocolDescription: string;
  setProtocolDescription: (value: string) => void;
  baseTemplate: string;
  setBaseTemplate: (value: string) => void;
  protocolStatus: 'ACTIVE' | 'ARCHIVED';
  setProtocolStatus: (value: 'ACTIVE' | 'ARCHIVED') => void;
  startDate: string;
  setStartDate: (value: string) => void;
  endDate: string;
  setEndDate: (value: string) => void;
  classifications: FoodClassification[];
  rules: ProtocolRule[];
  handleOpenFoodDialog: (food?: FoodClassification) => void;
  handleDeleteFood: (id: string) => void;
  handleOpenRuleDialog: (rule?: ProtocolRule) => void;
  handleDeleteRule: (id: string) => void;
  handleReset: () => void;
}

export const getProtocolBuilderStepContent = ({
  step,
  selectedPatient,
  setSelectedPatient,
  protocolTitle,
  setProtocolTitle,
  protocolDescription,
  setProtocolDescription,
  baseTemplate,
  setBaseTemplate,
  protocolStatus,
  setProtocolStatus,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  classifications,
  rules,
  handleOpenFoodDialog,
  handleDeleteFood,
  handleOpenRuleDialog,
  handleDeleteRule,
  handleReset,
}: StepContentArgs) => {
  switch (step) {
    case 0:
      return (
        <Box>
          <Typography variant="h6" gutterBottom>
            Select a patient to create a personalized protocol
          </Typography>
          <Grid container spacing={2} sx={{ mt: 2 }}>
            {patient360Data.assignedPatients.map((patient) => (
              <Grid item xs={12} sm={6} md={4} key={patient.id}>
                <Card
                  sx={{
                    cursor: 'pointer',
                    border: selectedPatient?.id === patient.id ? 2 : 1,
                    borderColor: selectedPatient?.id === patient.id ? 'primary.main' : 'divider',
                    '&:hover': {
                      boxShadow: 3,
                    },
                  }}
                  onClick={() => setSelectedPatient(patient)}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Box>
                        <Typography variant="h6">
                          {patient.firstName} {patient.lastName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          DOB: {patient.dateOfBirth}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Last Visit: {patient.lastVisit}
                        </Typography>
                      </Box>
                      {selectedPatient?.id === patient.id && <CheckCircleIcon color="primary" />}
                    </Box>
                    <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Chip
                        label={patient.riskLevel}
                        size="small"
                        color={
                          patient.riskLevel === 'LOW'
                            ? 'success'
                            : patient.riskLevel === 'MODERATE'
                            ? 'warning'
                            : 'error'
                        }
                      />
                      <Chip
                        label={patient.cgmStatus}
                        size="small"
                        color={patient.cgmStatus === 'ACTIVE' ? 'success' : 'default'}
                      />
                      <Chip label={`${patient.dataCompleteness}% Complete`} size="small" variant="outlined" />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      );

    case 1:
      return (
        <Box>
          <Typography variant="h6" gutterBottom>
            Protocol Basics
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Creating protocol for: <strong>{selectedPatient?.firstName} {selectedPatient?.lastName}</strong>
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Protocol Title"
                value={protocolTitle}
                onChange={(e) => setProtocolTitle(e.target.value)}
                placeholder="e.g., Personalized Type 2 Diabetes Protocol"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Description"
                value={protocolDescription}
                onChange={(e) => setProtocolDescription(e.target.value)}
                placeholder="Describe the goals and approach for this protocol..."
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Base Template</InputLabel>
                <Select value={baseTemplate} label="Base Template" onChange={(e) => setBaseTemplate(e.target.value)}>
                  <MenuItem value="template-standard-t2d">Standard Type 2 Diabetes</MenuItem>
                  <MenuItem value="template-prediabetes">Prediabetes Management</MenuItem>
                  <MenuItem value="template-gestational">Gestational Diabetes</MenuItem>
                  <MenuItem value="template-type1">Type 1 Diabetes</MenuItem>
                  <MenuItem value="template-custom">Custom (Start from Scratch)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={protocolStatus}
                  label="Status"
                  onChange={(e) => setProtocolStatus(e.target.value as 'ACTIVE' | 'ARCHIVED')}
                >
                  <MenuItem value="ACTIVE">Active</MenuItem>
                  <MenuItem value="ARCHIVED">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="End Date (Optional)"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </Grid>
          </Grid>
        </Box>
      );

    case 2:
      return (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Stoplight Food Classifications</Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenFoodDialog()}>
              Add Food
            </Button>
          </Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            Classify foods as GREEN (eat freely), YELLOW (eat in moderation), or RED (avoid/limit strictly).
            These are personalized based on {selectedPatient?.firstName}'s CGM data and response patterns.
          </Alert>
          {classifications.length === 0 ? (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">
                No food classifications added yet. Click "Add Food" to get started.
              </Typography>
            </Paper>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Food Item</TableCell>
                    <TableCell>Classification</TableCell>
                    <TableCell>Recommended Portion</TableCell>
                    <TableCell>Notes</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {classifications.map((food) => (
                    <TableRow key={food.id}>
                      <TableCell>{food.customFoodName}</TableCell>
                      <TableCell>
                        <Chip
                          label={food.classification}
                          size="small"
                          sx={{
                            bgcolor:
                              food.classification === 'GREEN'
                                ? 'success.main'
                                : food.classification === 'YELLOW'
                                ? 'warning.main'
                                : 'error.main',
                            color: 'white',
                          }}
                        />
                      </TableCell>
                      <TableCell>{food.portion}</TableCell>
                      <TableCell>{food.notes || '—'}</TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => handleOpenFoodDialog(food)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDeleteFood(food.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      );

    case 3:
      return (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Protocol Rules</Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenRuleDialog()}>
              Add Rule
            </Button>
          </Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            Define specific rules for meal timing, portion limits, food pairings, and avoidances.
          </Alert>
          {rules.length === 0 ? (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">
                No rules added yet. Click "Add Rule" to define protocol rules.
              </Typography>
            </Paper>
          ) : (
            <Grid container spacing={2}>
              {rules.map((rule) => (
                <Grid item xs={12} key={rule.id}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                            <Chip label={rule.ruleType} size="small" color="primary" />
                            {rule.isActive && <Chip label="Active" size="small" color="success" />}
                          </Box>
                          <Typography variant="body1" gutterBottom>
                            {rule.description}
                          </Typography>
                          {Object.keys(rule.parameters).length > 0 && (
                            <Typography variant="body2" color="text.secondary">
                              Parameters: {JSON.stringify(rule.parameters)}
                            </Typography>
                          )}
                        </Box>
                        <Box>
                          <IconButton size="small" onClick={() => handleOpenRuleDialog(rule)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => handleDeleteRule(rule.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      );

    case 4:
      return (
        <Box>
          <Typography variant="h6" gutterBottom>
            Review & Publish Protocol
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary">
                    Patient
                  </Typography>
                  <Typography variant="h6">
                    {selectedPatient?.firstName} {selectedPatient?.lastName}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary">
                    Protocol Details
                  </Typography>
                  <Typography variant="h6" gutterBottom>
                    {protocolTitle || 'Untitled Protocol'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {protocolDescription || 'No description provided'}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip label={`Base Template: ${baseTemplate}`} size="small" />
                    <Chip
                      label={`Status: ${protocolStatus === 'ACTIVE' ? 'Active' : 'Inactive'}`}
                      size="small"
                      color={protocolStatus === 'ACTIVE' ? 'success' : 'default'}
                    />
                    <Chip label={`Start: ${startDate || 'Not set'}`} size="small" variant="outlined" />
                    <Chip label={`End: ${endDate || 'Not set'}`} size="small" variant="outlined" />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Food Classifications
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <Chip
                      label={`${classifications.filter((f) => f.classification === 'GREEN').length} GREEN`}
                      size="small"
                      sx={{ bgcolor: 'success.main', color: 'white' }}
                    />
                    <Chip
                      label={`${classifications.filter((f) => f.classification === 'YELLOW').length} YELLOW`}
                      size="small"
                      sx={{ bgcolor: 'warning.main', color: 'white' }}
                    />
                    <Chip
                      label={`${classifications.filter((f) => f.classification === 'RED').length} RED`}
                      size="small"
                      sx={{ bgcolor: 'error.main', color: 'white' }}
                    />
                  </Box>
                  <Typography variant="body2">Total: {classifications.length} foods classified</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Protocol Rules
                  </Typography>
                  <Typography variant="body2">{rules.length} active rules defined</Typography>
                  <Box sx={{ mt: 2 }}>
                    {rules.map((rule) => (
                      <Chip key={rule.id} label={rule.ruleType} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      );

    case 5:
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Protocol Published Successfully!
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            The personalized protocol for {selectedPatient?.firstName} {selectedPatient?.lastName} has been activated.
          </Typography>
          <Box sx={{ mt: 4 }}>
            <Button variant="contained" onClick={handleReset} sx={{ mr: 2 }}>
              Create Another Protocol
            </Button>
            <Button variant="outlined">View Patient Profile</Button>
          </Box>
        </Box>
      );

    default:
      return 'Unknown step';
  }
};
